/**
 * Pinned Extensions (Gadgets / Apps / External apps) for the Ros rail.
 *
 * localStorage only for now; same key as the old Extensions card pin so existing
 * pins migrate. Shape upgraded from string[] ids to rich rows the rail can open
 * without a registry refetch. Backend persistence can replace this module later.
 */

import { useMemo, useSyncExternalStore } from 'react'
import { config } from './config/environment'
import type { RegistryEntry, RegistryKind } from './pages/registryApi'

export const PINNED_STORAGE_KEY = 'ros-extensions-pinned'

/** Shelf kinds that can appear in the rail Pinned section (not Skills). */
export type PinnedExtensionKind = 'gadget' | 'app' | 'external'

export interface PinnedExtension {
  id: string
  kind: PinnedExtensionKind
  name: string
  /** Cap'n Web definition id when url is /extensions/:id */
  definitionId?: string
  /** Registry launch url (may be relative); used for external / Studio apps */
  url?: string
  /** Cap'n Web shared App or Gadget: open beside chat */
  capnWeb?: boolean
}

type Listener = () => void
const listeners = new Set<Listener>()

function emit(): void {
  for (const l of listeners) l()
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  const onStorage = (e: StorageEvent) => {
    if (e.key === PINNED_STORAGE_KEY) listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

export function resolveExtensionUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url
  const path = url.startsWith('/') ? url : `/${url}`
  if (path === '/atr' || path.startsWith('/atr/')) return config.ATR_URL
  return `${config.STUDIO_URL.replace(/\/$/, '')}${path}`
}

export function gadgetDefinitionId(entry: Pick<RegistryEntry, 'id' | 'url'>): string {
  const m = entry.url.match(/^\/extensions\/([^/?#]+)/)
  if (m) return decodeURIComponent(m[1])
  const prefix = 'gadget-import-'
  if (entry.id.startsWith(prefix)) {
    const rest = entry.id.slice(prefix.length)
    const dash = rest.indexOf('-')
    if (dash >= 0) return rest.slice(dash + 1)
  }
  return entry.id
}

export function isCapnWebExtension(entry: Pick<RegistryEntry, 'url'>): boolean {
  return /^\/extensions\/[^/?#]+/.test(entry.url)
}

function tabKindToPinned(kind: RegistryKind | 'external'): PinnedExtensionKind {
  if (kind === 'gadget') return 'gadget'
  if (kind === 'app') return 'app'
  return 'external'
}

/** Build a pin row from a registry entry + Extensions shelf tab. */
export function pinnedFromRegistryEntry(
  entry: RegistryEntry,
  tab: 'gadget' | 'apps' | 'external',
): PinnedExtension {
  const kind: PinnedExtensionKind =
    tab === 'gadget' ? 'gadget' : tab === 'apps' ? 'app' : 'external'
  const capnWeb = isCapnWebExtension(entry)
  return {
    id: entry.id,
    kind,
    name: entry.name,
    definitionId: (tab === 'gadget' || capnWeb) ? gadgetDefinitionId(entry) : undefined,
    url: entry.url,
    capnWeb,
  }
}

function parseStored(raw: string | null): PinnedExtension[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    // Legacy: string[] of registry ids
    if (parsed.every((x) => typeof x === 'string')) {
      return (parsed as string[]).map((id) => ({
        id,
        kind: 'gadget' as const,
        name: id,
      }))
    }
    const out: PinnedExtension[] = []
    for (const row of parsed) {
      if (!row || typeof row !== 'object') continue
      const r = row as Record<string, unknown>
      if (typeof r.id !== 'string' || !r.id) continue
      const kind =
        r.kind === 'gadget' || r.kind === 'app' || r.kind === 'external'
          ? r.kind
          : tabKindToPinned(typeof r.kind === 'string' ? (r.kind as RegistryKind) : 'gadget')
      out.push({
        id: r.id,
        kind,
        name: typeof r.name === 'string' && r.name ? r.name : r.id,
        definitionId: typeof r.definitionId === 'string' ? r.definitionId : undefined,
        url: typeof r.url === 'string' ? r.url : undefined,
        capnWeb: r.capnWeb === true,
      })
    }
    return out
  } catch {
    return []
  }
}

let cachedRaw: string | null | undefined
let cachedList: PinnedExtension[] = []

function readList(): PinnedExtension[] {
  const raw = localStorage.getItem(PINNED_STORAGE_KEY)
  if (raw === cachedRaw) return cachedList
  cachedRaw = raw
  cachedList = parseStored(raw)
  return cachedList
}

function writeList(next: PinnedExtension[]): void {
  cachedRaw = JSON.stringify(next)
  cachedList = next
  localStorage.setItem(PINNED_STORAGE_KEY, cachedRaw)
  emit()
}

export function getPinnedExtensions(): PinnedExtension[] {
  return readList()
}

export function getPinnedIdSet(): Set<string> {
  return new Set(readList().map((p) => p.id))
}

export function isPinnedExtension(id: string): boolean {
  return readList().some((p) => p.id === id)
}

export function pinExtension(item: PinnedExtension): void {
  const list = readList()
  if (list.some((p) => p.id === item.id)) {
    writeList(list.map((p) => (p.id === item.id ? item : p)))
    return
  }
  writeList([...list, item])
}

export function unpinExtension(id: string): void {
  writeList(readList().filter((p) => p.id !== id))
}

export function togglePinnedExtension(item: PinnedExtension): void {
  if (isPinnedExtension(item.id)) unpinExtension(item.id)
  else pinExtension(item)
}

/**
 * When Extensions loads entries, refresh names/urls for any pins that match
 * (upgrades legacy id-only rows).
 */
export function enrichPinnedFromEntries(
  entries: RegistryEntry[],
  tab: 'gadget' | 'apps' | 'external',
): void {
  if (entries.length === 0) return
  const byId = new Map(entries.map((e) => [e.id, e]))
  let changed = false
  const next = readList().map((pin) => {
    const entry = byId.get(pin.id)
    if (!entry) return pin
    const enriched = pinnedFromRegistryEntry(entry, tab)
    if (
      enriched.name !== pin.name ||
      enriched.url !== pin.url ||
      enriched.definitionId !== pin.definitionId ||
      enriched.capnWeb !== pin.capnWeb ||
      enriched.kind !== pin.kind
    ) {
      changed = true
      return enriched
    }
    return pin
  })
  if (changed) writeList(next)
}

export function usePinnedExtensions(): PinnedExtension[] {
  return useSyncExternalStore(subscribe, getPinnedExtensions, () => [])
}

export function usePinnedIdSet(): Set<string> {
  const list = usePinnedExtensions()
  return useMemo(() => new Set(list.map((p) => p.id)), [list])
}
